import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@env/environment';
import { PaymentInitApiResponse, PaymentInitRequest } from '@lib/interfaces/payment.interface';
import { ErrorHandlerService } from '@lib/services/error/error-handler.service';
import { Observable, catchError, throwError } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class PaymentService {
    private readonly http = inject(HttpClient);
    private readonly baseUrl = environment.API_URL;
    private readonly errorHandlerService = inject(ErrorHandlerService);

    /**
     * Initialize payment for both SELF and COMPANY payment types
     * @param enrollmentId - The enrollment ID
     * @param paymentRequest - Payment request data (built in component)
     * @returns Observable of payment init response
     */
    initializePayment(
        enrollmentId: string,
        paymentRequest: PaymentInitRequest,
        forcePayment: boolean = false,
    ): Observable<PaymentInitApiResponse> {
        return this.http
            .post<PaymentInitApiResponse>(`${this.baseUrl}/students/${enrollmentId}/payment/init`, paymentRequest, {
                params: {
                    force: forcePayment,
                },
            })
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    console.error('Payment initialization failed:', error);

                    // Use enhanced error handler for consistent error messages
                    const errorMessage = this.errorHandlerService.getErrorMessage(error, {
                        context: 'payment',
                        defaultMessage: 'Payment initialization failed. Please try again.',
                        fallbackToApiMessage: true,
                        showFriendlyMessage: true,
                    });

                    // Ensure we always have a valid error message string
                    const finalErrorMessage = errorMessage || 'Payment initialization failed. Please try again.';

                    return throwError(() => new Error(finalErrorMessage, { cause: error.status }));
                }),
            );
    }

    /**
     * Handle immediate response from payment gateway
     * @param sMsg - Payment response message from gateway
     * @returns Observable of payment response with enrollmentId and status
     */
    handleImmediateResponse(sMsg: string): Observable<{ enrollmentId: string; status: 'success' | 'failed' }> {
        return this.http
            .get<{
                enrollmentId: string;
                status: 'success' | 'failed';
            }>(`${this.baseUrl}/payment/immediate-response`, { params: { sMsg } })
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    console.error('Payment immediate response handling failed:', error);

                    // Use enhanced error handler for consistent error messages
                    const errorMessage = this.errorHandlerService.getErrorMessage(error, {
                        context: 'payment',
                        defaultMessage: 'Failed to process payment response. Please contact support.',
                        fallbackToApiMessage: true,
                        showFriendlyMessage: true,
                    });

                    // Ensure we always have a valid error message string
                    const finalErrorMessage =
                        errorMessage || 'Failed to process payment response. Please contact support.';

                    return throwError(() => new Error(finalErrorMessage));
                }),
            );
    }
}
